import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { getStudentStats, getAllQuizResults, getReviewerById, getPlatformAnalytics, deleteQuizResult, batchDeleteQuizResults } from "../../services/api";
import Swal from "sweetalert2";

import {
  formatStudyTime,
  formatDuration,
  formatDate,
  DEFAULT_WEEK,
  getStartOfCurrentWeek,
  coerceToThisWeek,
  getPercentage,
  getModeLabel,
  buildWeeklyFromHistoryLast7Days,
  normalizeStats,
  extractReviewerTitle,
  extractReviewerId,
} from "../../components/Analytics/analyticsUtils";

import StatCard from "../../components/Analytics/StatCard";
import BarGroup from "../../components/Analytics/BarGroup";
import LegendBadge from "../../components/Analytics/LegendBadge";
import { HistoryRow } from "../../components/Analytics/HistoryRows";
import HistoryModal from "../../components/Analytics/HistoryModal";
import { PlatformPill, PlatformTopicRow, TrendBar } from "../../components/Analytics/PlatformComponents";

const Analytics = () => {
  const navigate = useNavigate();
  const HISTORY_PREVIEW_COUNT = 8;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [reviewerTitleMap, setReviewerTitleMap] = useState({});
  const [loadingReviewerIds, setLoadingReviewerIds] = useState(() => new Set());

  const [weeklyMaxSeconds, setWeeklyMaxSeconds] = useState(60);

  const [dashboardData, setDashboardData] = useState({
    quizAverage: 0,
    examAverage: 0,
    totalSeconds: 0,
    quizHours: 0,
    readingHours: 0,
    flashcardHours: 0,
    streak: 0,
    readiness: "Low",
    weeklyActivity: DEFAULT_WEEK,
    topicActivityThisWeek: [],
    recentHistory: [],
  });

  const [platformData, setPlatformData] = useState(null);
  const [isPlatformLoading, setIsPlatformLoading] = useState(true);

  const toggleSidebar = () => setIsSidebarOpen((v) => !v);

  // -----------------------------
  // 1) Fetch stats + results
  // -----------------------------
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [statsRes, historyRes] = await Promise.allSettled([
          getStudentStats(),
          getAllQuizResults(),
        ]);

        const statsRaw = statsRes.status === "fulfilled" ? statsRes.value : null;
        const stats = normalizeStats(statsRaw);

        const historyRaw = historyRes.status === "fulfilled" ? historyRes.value : [];
        const history = Array.isArray(historyRaw) ? historyRaw : [];

        // ---------------------------
        // Quiz Average (overall)
        // ---------------------------
        let quizAverage = 0;
        const validScoresAll = history.map(getPercentage).filter((p) => p !== null);
        if (validScoresAll.length > 0) {
          quizAverage = Math.round(validScoresAll.reduce((a, b) => a + b, 0) / validScoresAll.length);
        }

        // ---------------------------
        // ✅ Exam Average (exam mode only)
        // ---------------------------
        const examOnly = history.filter((h) => String(h?.mode || "").toLowerCase() === "exam");
        const validExamScores = examOnly.map(getPercentage).filter((p) => p !== null);

        let examAverage = 0;
        if (validExamScores.length > 0) {
          examAverage = Math.round(validExamScores.reduce((a, b) => a + b, 0) / validExamScores.length);
        }

        // ---------------------------
        // ✅ Readiness based on EXAM average only
        // ---------------------------
        let readiness = "Low";
        if (validExamScores.length > 0) {
          if (examAverage >= 80) readiness = "High";
          else if (examAverage >= 60) readiness = "Medium";
          else readiness = "Low";
        } else {
          readiness = "Low";
        }

        // ---------------------------
        // ✅ THIS WEEK ONLY chart
        // ---------------------------
        const weeklyActivity =
          stats?.weeklyActivity?.length > 0
            ? coerceToThisWeek(stats.weeklyActivity)
            : buildWeeklyFromHistoryLast7Days(history);

        const maxSecs = Math.max(
          60,
          ...weeklyActivity.map((d) => (Number(d.quizSeconds) || 0) + (Number(d.readSeconds) || 0))
        );
        setWeeklyMaxSeconds(maxSecs);

        setDashboardData({
          quizAverage,
          examAverage,
          totalSeconds: stats?.totalSeconds ?? 0,
          quizHours: stats?.quizTotalHours ?? 0,
          readingHours: stats?.readingTotalHours ?? 0,
          flashcardHours: stats?.flashcardTotalHours ?? 0,
          streak: stats?.streak ?? 0,
          readiness,
          weeklyActivity,
          topicActivityThisWeek: Array.isArray(stats?.topicActivityThisWeek)
            ? stats.topicActivityThisWeek
            : [],
          recentHistory: history,
        });
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setIsLoading(false);
        setTimeout(() => setAnimate(true), 300);
      }
    };

    fetchData();
  }, []);

  // -----------------------------
  // 1b) Fetch platform analytics
  // -----------------------------
  useEffect(() => {
    const fetchPlatform = async () => {
      setIsPlatformLoading(true);
      try {
        const res = await getPlatformAnalytics();
        if (res?.success && res?.data) setPlatformData(res.data);
      } catch (err) {
        console.error("Failed to fetch platform analytics:", err);
      } finally {
        setIsPlatformLoading(false);
      }
    };
    fetchPlatform();
  }, []);

  // -----------------------------
  // 2) Resolve reviewer titles
  // -----------------------------
  const reviewerIdsInHistory = useMemo(() => {
    const history = dashboardData.recentHistory || [];
    return Array.from(
      new Set(
        history
          .map((h) => extractReviewerId(h))
          .filter((rid) => typeof rid === "string" && rid.trim())
      )
    );
  }, [dashboardData.recentHistory]);

  useEffect(() => {
    if (reviewerIdsInHistory.length === 0) return;

    const missingIds = reviewerIdsInHistory.filter(
      (rid) => !reviewerTitleMap[rid] && !loadingReviewerIds.has(rid)
    );
    if (missingIds.length === 0) return;

    (async () => {
      setLoadingReviewerIds((prev) => {
        const next = new Set(prev);
        missingIds.forEach((id) => next.add(id));
        return next;
      });

      const fetched = await Promise.allSettled(
        missingIds.map(async (rid) => {
          const res = await getReviewerById(rid);
          const title = extractReviewerTitle(res) || "Deleted Reviewer";
          return { rid, title };
        })
      );

      const updates = {};
      fetched.forEach((r) => {
        if (r.status === "fulfilled") updates[r.value.rid] = r.value.title;
      });

      if (Object.keys(updates).length > 0) setReviewerTitleMap((prev) => ({ ...prev, ...updates }));

      setLoadingReviewerIds((prev) => {
        const next = new Set(prev);
        missingIds.forEach((id) => next.delete(id));
        return next;
      });
    })();
  }, [reviewerIdsInHistory, reviewerTitleMap, loadingReviewerIds]);

  const getReviewerTitle = (item) => {
    if (item?.reviewerTitle) return item.reviewerTitle;
    if (item?.topic) return item.topic;

    const r = item?.reviewerId;

    if (r && typeof r === "object") {
      return r.topic || r.title || r.name || r.subject || "Unknown Reviewer";
    }

    if (typeof r === "string" && r.trim()) {
      if (reviewerTitleMap[r]) return reviewerTitleMap[r];
      if (loadingReviewerIds.has(r)) return "Loading...";
      return "Deleted Reviewer";
    }

    return "Unknown Reviewer";
  };

  const handleReviewModule = (item) => {
    const reviewerId = extractReviewerId(item);
    const title = getReviewerTitle(item);

    if (!reviewerId || title === "Deleted Reviewer") {
      Swal.fire({
        icon: "warning",
        title: "Module Unavailable",
        text: "This reviewer or module is no longer available or was deleted.",
        confirmButtonColor: "#4F46E5",
        confirmButtonText: "Go to Library",
        showCancelButton: true,
        cancelButtonText: "Close",
      }).then((res) => {
        if (res.isConfirmed) {
          navigate("/library");
        }
      });
      return;
    }

    navigate(`/study-mode/${reviewerId}`, {
      state: {
        reviewerId,
        mode: "summary",
        noteData: {
          id: reviewerId,
          _id: reviewerId,
          title: title !== "Loading..." && title !== "Unknown Reviewer" ? title : undefined,
        },
      },
    });
  };

  const handleDeleteResult = (resultId) => {
    if (!resultId) return;

    Swal.fire({
      title: "Remove Attempt?",
      text: "Are you sure you want to remove this quiz attempt from your analytics? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#94A3B8",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteQuizResult(resultId);

          setDashboardData((prev) => {
            const updatedHistory = (prev.recentHistory || []).filter(
              (item) => String(item._id) !== String(resultId)
            );

            // Recalculate Quiz Average
            let quizAverage = 0;
            const validScoresAll = updatedHistory.map(getPercentage).filter((p) => p !== null);
            if (validScoresAll.length > 0) {
              quizAverage = Math.round(validScoresAll.reduce((a, b) => a + b, 0) / validScoresAll.length);
            }

            // Recalculate Exam Average
            const examOnly = updatedHistory.filter((h) => String(h?.mode || "").toLowerCase() === "exam");
            const validExamScores = examOnly.map(getPercentage).filter((p) => p !== null);
            let examAverage = 0;
            if (validExamScores.length > 0) {
              examAverage = Math.round(validExamScores.reduce((a, b) => a + b, 0) / validExamScores.length);
            }

            // Recalculate Readiness
            let readiness = "Low";
            if (validExamScores.length > 0) {
              if (examAverage >= 80) readiness = "High";
              else if (examAverage >= 60) readiness = "Medium";
              else readiness = "Low";
            }

            // Recalculate Weekly Activity if fallback was used
            let weeklyActivity = prev.weeklyActivity;
            if (!prev.weeklyActivity || prev.weeklyActivity === DEFAULT_WEEK) {
              weeklyActivity = buildWeeklyFromHistoryLast7Days(updatedHistory);
            }

            return {
              ...prev,
              quizAverage,
              examAverage,
              readiness,
              weeklyActivity,
              recentHistory: updatedHistory,
            };
          });

          // Clear deleted ID from selection if selected
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(resultId);
            return next;
          });

          // Refresh stats in background to keep study logs/hours synced
          getStudentStats().then((statsRes) => {
            if (statsRes) {
              const stats = normalizeStats(statsRes);
              setDashboardData((prev) => ({
                ...prev,
                totalSeconds: stats?.totalSeconds ?? prev.totalSeconds,
                quizHours: stats?.quizTotalHours ?? prev.quizHours,
                readingHours: stats?.readingTotalHours ?? prev.readingHours,
                flashcardHours: stats?.flashcardTotalHours ?? prev.flashcardHours,
                streak: stats?.streak ?? prev.streak,
              }));
            }
          }).catch(() => {});

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Quiz attempt has been removed.",
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Failed to delete quiz result:", error);
          Swal.fire({
            icon: "error",
            title: "Delete Failed",
            text: error?.message || "Failed to remove the quiz attempt.",
          });
        }
      }
    });
  };

  const handleBatchDelete = (idsToDelete, onSuccess) => {
    const list = Array.isArray(idsToDelete) ? idsToDelete.filter(Boolean) : [];
    if (list.length === 0) return;

    const count = list.length;
    Swal.fire({
      title: `Delete ${count} Attempt${count > 1 ? "s" : ""}?`,
      text: `Are you sure you want to remove ${count} selected quiz attempt${count > 1 ? "s" : ""} from your analytics? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#94A3B8",
      confirmButtonText: `Yes, delete ${count > 1 ? `all ${count}` : "it"}!`,
      cancelButtonText: "Cancel",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await batchDeleteQuizResults(list);

          const idsSet = new Set(list.map(String));

          setDashboardData((prev) => {
            const updatedHistory = (prev.recentHistory || []).filter(
              (item) => !idsSet.has(String(item._id))
            );

            // Recalculate Quiz Average
            let quizAverage = 0;
            const validScoresAll = updatedHistory.map(getPercentage).filter((p) => p !== null);
            if (validScoresAll.length > 0) {
              quizAverage = Math.round(validScoresAll.reduce((a, b) => a + b, 0) / validScoresAll.length);
            }

            // Recalculate Exam Average
            const examOnly = updatedHistory.filter((h) => String(h?.mode || "").toLowerCase() === "exam");
            const validExamScores = examOnly.map(getPercentage).filter((p) => p !== null);
            let examAverage = 0;
            if (validExamScores.length > 0) {
              examAverage = Math.round(validExamScores.reduce((a, b) => a + b, 0) / validExamScores.length);
            }

            // Recalculate Readiness
            let readiness = "Low";
            if (validExamScores.length > 0) {
              if (examAverage >= 80) readiness = "High";
              else if (examAverage >= 60) readiness = "Medium";
              else readiness = "Low";
            }

            // Recalculate Weekly Activity if fallback was used
            let weeklyActivity = prev.weeklyActivity;
            if (!prev.weeklyActivity || prev.weeklyActivity === DEFAULT_WEEK) {
              weeklyActivity = buildWeeklyFromHistoryLast7Days(updatedHistory);
            }

            return {
              ...prev,
              quizAverage,
              examAverage,
              readiness,
              weeklyActivity,
              recentHistory: updatedHistory,
            };
          });

          // Clear local selected
          setSelectedIds((prev) => {
            const next = new Set(prev);
            list.forEach((id) => next.delete(id));
            return next;
          });

          if (typeof onSuccess === "function") {
            onSuccess();
          }

          // Refresh logs stats in background
          getStudentStats().then((statsRes) => {
            if (statsRes) {
              const stats = normalizeStats(statsRes);
              setDashboardData((prev) => ({
                ...prev,
                totalSeconds: stats?.totalSeconds ?? prev.totalSeconds,
                quizHours: stats?.quizTotalHours ?? prev.quizHours,
                readingHours: stats?.readingTotalHours ?? prev.readingHours,
                flashcardHours: stats?.flashcardTotalHours ?? prev.flashcardHours,
                streak: stats?.streak ?? prev.streak,
              }));
            }
          }).catch(() => {});

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: `${count} quiz attempt${count > 1 ? "s have" : " has"} been removed.`,
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Failed to batch delete quiz results:", error);
          Swal.fire({
            icon: "error",
            title: "Delete Failed",
            text: error?.message || "Failed to remove the selected quiz attempts.",
          });
        }
      }
    });
  };

  const historyRows = dashboardData.recentHistory || [];
  const hasMoreHistory = historyRows.length > HISTORY_PREVIEW_COUNT;
  const displayedHistory = showAllHistory
    ? historyRows
    : historyRows.slice(0, HISTORY_PREVIEW_COUNT);

  const displayedRows = historyRows.slice(0, HISTORY_PREVIEW_COUNT);
  const displayedRowIds = displayedRows.map((r) => r._id).filter(Boolean);
  const isAllDisplayedSelected =
    displayedRowIds.length > 0 && displayedRowIds.every((id) => selectedIds.has(id));

  const toggleSelectAllDisplayed = () => {
    if (isAllDisplayedSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedRowIds));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const weeklyTopicDetails = useMemo(() => {
    if (Array.isArray(dashboardData.topicActivityThisWeek) && dashboardData.topicActivityThisWeek.length > 0) {
      return dashboardData.topicActivityThisWeek
        .map((item) => ({
          topic: item?.topic || "Unknown Reviewer",
          totalSeconds: Math.max(0, Number(item?.totalSeconds) || 0),
        }))
        .filter((item) => item.totalSeconds > 0)
        .sort((a, b) => b.totalSeconds - a.totalSeconds);
    }

    const startOfWeek = getStartOfCurrentWeek();
    const now = new Date();
    const totalsByTopic = {};

    historyRows.forEach((item) => {
      const createdAt = new Date(item?.createdAt || "");
      if (isNaN(createdAt.getTime())) return;
      if (createdAt < startOfWeek || createdAt > now) return;

      const topic =
        item?.reviewerTitle ||
        item?.topic ||
        (item?.reviewerId && typeof item.reviewerId === "object"
          ? item.reviewerId.topic ||
            item.reviewerId.title ||
            item.reviewerId.name ||
            item.reviewerId.subject
          : typeof item?.reviewerId === "string" && item.reviewerId.trim()
            ? reviewerTitleMap[item.reviewerId] ||
              (loadingReviewerIds.has(item.reviewerId) ? "Loading..." : "Deleted Reviewer")
            : "Unknown Reviewer") ||
        "Unknown Reviewer";
      const spent = Math.max(0, Number(item?.timeSpentSeconds) || 0);
      totalsByTopic[topic] = (totalsByTopic[topic] || 0) + spent;
    });

    return Object.entries(totalsByTopic)
      .map(([topic, totalSeconds]) => ({ topic, totalSeconds }))
      .sort((a, b) => b.totalSeconds - a.totalSeconds);
  }, [dashboardData.topicActivityThisWeek, historyRows, reviewerTitleMap, loadingReviewerIds]);

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title="Performance Analytics" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full space-y-6 animate-fade-in pb-20 md:pb-0">
            

            {/* STATS GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" role="region" aria-label="Key Performance Indicators">
              <StatCard
                icon="fa-graduation-cap"
                color="blue"
                title="Quiz Average"
                value={`${dashboardData.quizAverage}%`}
                subtext="Overall answer accuracy"
              />
              <StatCard
                icon="fa-clock"
                color="green"
                title="Total Study Time"
                value={formatStudyTime(dashboardData.totalSeconds)}
                subtext="Total invested in review"
              />
              <StatCard
                icon="fa-stopwatch"
                color="red"
                title="Exam Readiness"
                value={dashboardData.readiness}
                subtext={
                  dashboardData.examAverage > 0
                    ? `Exam Score: ${dashboardData.examAverage}%`
                    : "Complete an exam to calculate"
                }
              />
              <StatCard
                icon="fa-fire"
                color="orange"
                title="Active Streak"
                value={`${dashboardData.streak} Days`}
                subtext="Consecutive study days"
                trend={dashboardData.streak > 0 ? "up" : "neutral"}
              />
            </div>

            {/* WEEKLY CHART */}
            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-gray-800 text-lg">This Week Activity</h3>
                <div className="flex items-center gap-4">
                  <span className="flex items-center text-xs text-gray-500">
                    <span className="w-2.5 h-2.5 rounded-sm bg-brand-500 mr-2"></span> Quiz Time
                  </span>
                  <span className="flex items-center text-xs text-gray-500">
                    <span className="w-2.5 h-2.5 rounded-sm bg-purple-400 mr-2"></span> Reading Time
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowWeekDetails((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-brand-600 bg-brand-50 hover:bg-brand-100 transition-all border border-brand-200/60 shadow-2xs cursor-pointer"
                  >
                    <i className={`fa-solid ${showWeekDetails ? "fa-chevron-up" : "fa-chart-pie"} text-[10px]`}></i>
                    <span>{showWeekDetails ? "Hide Topic Breakdown" : "View Topic Breakdown"}</span>
                  </button>
                </div>
              </div>

              <div className="h-64 flex items-end justify-between px-2 gap-2 md:gap-4 border-b border-gray-100 pb-2">
                {dashboardData.weeklyActivity.map((dayData, index) => (
                  <BarGroup key={index} dayData={dayData} animate={animate} maxSeconds={weeklyMaxSeconds} />
                ))}
              </div>

              {isLoading && (
                <div className="text-center text-sm text-gray-400 mt-4">Loading weekly activity...</div>
              )}

              {showWeekDetails && (
                <div className="mt-6 border-t border-slate-100 pt-5 animate-in fade-in slide-in-from-top-2 duration-300">
                  {(() => {
                    const totalWeeklyTopicSeconds = weeklyTopicDetails.reduce(
                      (acc, curr) => acc + curr.totalSeconds,
                      0
                    );
                    const maxTopicSeconds = weeklyTopicDetails.length > 0 ? weeklyTopicDetails[0].totalSeconds : 1;

                    return (
                      <>
                        {/* Section Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-600 text-white flex items-center justify-center text-xs shadow-sm shadow-brand-500/20">
                              <i className="fa-solid fa-chart-pie"></i>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-bold text-slate-800">Time Spent by Topic</h4>
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100">
                                  This Week
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-400">Reviewers with active study &amp; quiz sessions</p>
                            </div>
                          </div>

                          {weeklyTopicDetails.length > 0 && (
                            <div className="flex items-center gap-2 self-start sm:self-auto text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200/70 px-3 py-1.5 rounded-xl shadow-2xs">
                              <span className="text-slate-400 font-medium">Total:</span>
                              <span className="font-extrabold text-slate-900">{formatDuration(totalWeeklyTopicSeconds)}</span>
                              <span className="text-slate-300">•</span>
                              <span className="text-slate-500">
                                {weeklyTopicDetails.length} {weeklyTopicDetails.length === 1 ? "topic" : "topics"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* List Content */}
                        {isLoading ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[1, 2].map((i) => (
                              <div key={i} className="animate-pulse bg-slate-50 border border-slate-100 rounded-xl p-3.5 space-y-2">
                                <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                                <div className="h-2 bg-slate-200 rounded w-full"></div>
                              </div>
                            ))}
                          </div>
                        ) : weeklyTopicDetails.length === 0 ? (
                          <div className="p-8 text-center bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-400 flex items-center justify-center mx-auto mb-3 text-lg">
                              <i className="fa-solid fa-clock-rotate-left"></i>
                            </div>
                            <p className="text-sm font-bold text-slate-700">No Reviewer Time Tracked This Week</p>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                              Open your reviewers in Study mode or complete quizzes this week to visualize your time investment.
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {weeklyTopicDetails.map((item, idx) => {
                              const percent = totalWeeklyTopicSeconds > 0
                                ? Math.round((item.totalSeconds / totalWeeklyTopicSeconds) * 100)
                                : 0;
                              const barWidth = maxTopicSeconds > 0
                                ? Math.max(5, Math.round((item.totalSeconds / maxTopicSeconds) * 100))
                                : 0;

                              const rankBadges = [
                                { label: "1", bg: "bg-amber-500 text-white shadow-xs shadow-amber-500/20" },
                                { label: "2", bg: "bg-slate-700 text-white" },
                                { label: "3", bg: "bg-amber-700 text-white" },
                              ];
                              const rankBadge = rankBadges[idx] || {
                                label: `${idx + 1}`,
                                bg: "bg-slate-100 text-slate-600 border border-slate-200",
                              };

                              return (
                                <div
                                  key={item.topic}
                                  className="group relative bg-white hover:bg-slate-50/80 border border-slate-200/80 hover:border-brand-300 rounded-xl p-3.5 transition-all duration-200 shadow-2xs hover:shadow-md flex flex-col justify-between"
                                >
                                  <div>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <span
                                          className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${rankBadge.bg}`}
                                        >
                                          {rankBadge.label}
                                        </span>
                                        <div className="min-w-0">
                                          <h5
                                            className="text-sm font-bold text-slate-800 group-hover:text-brand-600 transition-colors truncate"
                                            title={item.topic}
                                          >
                                            {item.topic}
                                          </h5>
                                          <span className="text-[10px] font-semibold text-slate-400">
                                            {percent}% of weekly study
                                          </span>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-extrabold text-xs bg-slate-100 text-slate-800 group-hover:bg-brand-50 group-hover:text-brand-700 group-hover:border-brand-200 border border-transparent transition-all shrink-0">
                                        <i className="fa-regular fa-clock text-[11px] text-brand-500"></i>
                                        <span>{formatDuration(item.totalSeconds)}</span>
                                      </div>
                                    </div>

                                    {/* Progress Bar Track */}
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1.5">
                                      <div
                                        className="h-full rounded-full bg-gradient-to-r from-brand-500 to-indigo-500 transition-all duration-700 ease-out group-hover:from-brand-600 group-hover:to-indigo-600"
                                        style={{ width: `${barWidth}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* HISTORY TABLE */}
            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-800 text-lg">Recent Quiz History</h3>
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  disabled={!hasMoreHistory}
                  className={`text-xs font-bold flex items-center gap-1.5 ${
                    hasMoreHistory
                      ? "text-brand-600 hover:text-brand-800"
                      : "text-gray-300 cursor-not-allowed"
                  }`}
                >
                  <i className="fa-solid fa-expand text-[10px]" />
                  View All History
                </button>
              </div>

              {/* Score Legend & Bulk Action */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mr-1">Legend:</span>
                  <LegendBadge color="green" label="Mastered" range="≥ 80%" />
                  <LegendBadge color="orange" label="Progressing" range="60–79%" />
                  <LegendBadge color="red" label="Needs Review" range="< 60%" />
                </div>

                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-xl animate-fadeIn">
                    <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                      <i className="fa-solid fa-check-double text-rose-600" />
                      <span>{selectedIds.size} selected</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      className="px-2 py-0.5 text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded hover:bg-slate-50 transition"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBatchDelete(Array.from(selectedIds))}
                      className="px-2.5 py-1 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded shadow-xs flex items-center gap-1.5 transition active:scale-95"
                    >
                      <i className="fa-solid fa-trash-can text-xs" />
                      Delete Selected ({selectedIds.size})
                    </button>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse" aria-label="Recent Quiz Attempts Table">
                  <thead>
                    <tr className="text-xs text-slate-700 bg-slate-50 border-b border-slate-200 uppercase tracking-wider font-bold">
                      <th scope="col" className="py-3.5 pl-4 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={isAllDisplayedSelected}
                          onChange={toggleSelectAllDisplayed}
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer transition"
                          aria-label="Select all displayed attempts"
                        />
                      </th>
                      <th scope="col" className="py-3.5 px-3">Subject &amp; Reviewer</th>
                      <th scope="col" className="py-3.5 px-3">Date Completed</th>
                      <th scope="col" className="py-3.5 px-3">Session Type</th>
                      <th scope="col" className="py-3.5 px-3">Accuracy Score</th>
                      <th scope="col" className="py-3.5 px-3">Proficiency Status</th>
                      <th scope="col" className="py-3.5 pr-4 text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody className="text-sm divide-y divide-slate-100">
                    {isLoading ? (
                      <tr><td colSpan="7" className="py-12 text-center text-slate-500 font-medium">Loading analytics history...</td></tr>
                    ) : historyRows.length === 0 ? (
                      <tr><td colSpan="7" className="py-12 text-center text-slate-500 font-medium">No quizzes taken yet. Complete your first review to see results!</td></tr>
                    ) : (
                      displayedRows.map((item, idx) => {
                        const pct = getPercentage(item) || 0;
                        const statusColor = pct >= 80 ? "green" : pct >= 60 ? "orange" : "red";
                        const status = pct >= 80 ? "Mastered" : pct >= 60 ? "Progressing" : "Needs Review";

                        return (
                          <HistoryRow
                            key={item._id || idx}
                            subject={getReviewerTitle(item)}
                            icon="fa-file-lines"
                            date={formatDate(item.createdAt)}
                            type={getModeLabel(item.mode)}
                            score={`${pct}%`}
                            status={status}
                            statusColor={statusColor}
                            isSelected={selectedIds.has(item._id)}
                            onToggleSelect={() => toggleSelectOne(item._id)}
                            onReview={() => handleReviewModule(item)}
                            onStatusClick={() => handleReviewModule(item)}
                            onTitleClick={() => handleReviewModule(item)}
                            onDelete={() => handleDeleteResult(item._id)}
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* HISTORY MODAL */}
            {showHistoryModal && (
              <HistoryModal
                historyRows={historyRows}
                getReviewerTitle={getReviewerTitle}
                onClose={() => setShowHistoryModal(false)}
                onDeleteResult={handleDeleteResult}
                onBatchDelete={handleBatchDelete}
                onReviewModule={handleReviewModule}
              />
            )}

            {/* PLATFORM TRENDS */}
            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm">
                    <i className="fa-solid fa-chart-column" />
                  </span>
                  Platform Learning Trends
                </h3>
                <p className="text-xs text-gray-400 mt-1 ml-10">Most-studied topics across all ReviewMate students</p>
              </div>

              {/* Summary stat pills */}
              {!isPlatformLoading && platformData?.summary && (
                <div className="flex flex-wrap gap-3 mb-6">
                  <PlatformPill icon="fa-book-open" color="blue" label="Total Reviewers" value={platformData.summary.totalReviewers.toLocaleString()} />
                  <PlatformPill icon="fa-users" color="purple" label="Active Students" value={platformData.summary.totalStudents.toLocaleString()} />
                  <PlatformPill icon="fa-pen-nib" color="green" label="Quizzes Taken" value={platformData.summary.totalQuizzes.toLocaleString()} />
                </div>
              )}

              {isPlatformLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : !platformData?.topTopics?.length ? (
                <div className="text-center py-10 text-gray-400 text-sm">No platform data available yet.</div>
              ) : (
                <div className="space-y-1">
                  {/* Header row */}
                  <div className="grid grid-cols-12 text-[10px] font-bold text-gray-400 uppercase tracking-wide px-3 pb-2">
                    <span className="col-span-1">#</span>
                    <span className="col-span-4">Topic</span>
                    <span className="col-span-3 text-center">Reviewers</span>
                    <span className="col-span-2 text-center">Students</span>
                    <span className="col-span-2 text-center">Quizzes</span>
                  </div>

                  {platformData.topTopics.map((item, idx) => (
                    <PlatformTopicRow key={item.topic} rank={idx + 1} item={item}
                      maxCount={platformData.topTopics[0]?.reviewerCount || 1} />
                  ))}
                </div>
              )}

              {/* Monthly creation trend */}
              {!isPlatformLoading && platformData?.creationTrend?.length > 0 && (
                <div className="mt-8 border-t border-gray-100 pt-6">
                  <h4 className="text-sm font-bold text-gray-700 mb-4">Reviewer Creation Trend (Last 6 Months)</h4>
                  <div className="flex items-end gap-2 h-28">
                    {platformData.creationTrend.map((m) => (
                      <TrendBar key={m.month} month={m.month} count={m.count}
                        maxCount={Math.max(...platformData.creationTrend.map((x) => x.count), 1)} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* COURSE DISTRIBUTION + INTEREST TRENDS row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* ── Course Distribution ── */}
              <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                <div className="mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 text-sm shrink-0">
                    <i className="fa-solid fa-graduation-cap" />
                  </span>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base leading-tight">Course Distribution</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">Students by program/course</p>
                  </div>
                </div>

                {isPlatformLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : !platformData?.courseStats?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                    <i className="fa-solid fa-book-open text-2xl text-gray-200" />
                    <p className="text-sm">No course data yet.</p>
                    <p className="text-xs text-gray-300">Students haven't set their course in Profile.</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {platformData.courseStats.map((item, idx) => {
                      const max = platformData.courseStats[0]?.studentCount || 1;
                      const pct = Math.round((item.studentCount / max) * 100);
                      const colors = [
                        "from-blue-500 to-blue-400",
                        "from-indigo-500 to-indigo-400",
                        "from-violet-500 to-violet-400",
                        "from-sky-500 to-sky-400",
                        "from-cyan-500 to-cyan-400",
                      ];
                      const color = colors[idx % colors.length];
                      return (
                        <div key={item.course}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-700 truncate max-w-[65%]" title={item.course}>
                              {item.course}
                            </span>
                            <span className="text-xs font-bold text-gray-500">
                              {item.studentCount} {item.studentCount === 1 ? "student" : "students"}
                            </span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-700`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Subject Interest Trends ── */}
              <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                <div className="mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 text-sm shrink-0">
                    <i className="fa-solid fa-tags" />
                  </span>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base leading-tight">Subject Interest Trends</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">Most declared interests across all students</p>
                  </div>
                </div>

                {isPlatformLoading ? (
                  <div className="flex flex-wrap gap-2">
                    {[...Array(10)].map((_, i) => (
                      <div key={i} className="h-7 rounded-full bg-gray-100 animate-pulse" style={{ width: `${60 + (i * 17) % 80}px` }} />
                    ))}
                  </div>
                ) : !platformData?.interestStats?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                    <i className="fa-solid fa-tag text-2xl text-gray-200" />
                    <p className="text-sm">No interest data yet.</p>
                    <p className="text-xs text-gray-300">Students haven't added interests in Profile.</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {platformData.interestStats.map((item, idx) => {
                      const max = platformData.interestStats[0]?.studentCount || 1;
                      const pct = Math.round((item.studentCount / max) * 100);
                      const tagColors = [
                        { bar: "from-purple-500 to-purple-400", tag: "bg-purple-50 text-purple-700 border-purple-100" },
                        { bar: "from-fuchsia-500 to-fuchsia-400", tag: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100" },
                        { bar: "from-violet-500 to-violet-400", tag: "bg-violet-50 text-violet-700 border-violet-100" },
                        { bar: "from-pink-500 to-pink-400", tag: "bg-pink-50 text-pink-700 border-pink-100" },
                        { bar: "from-rose-500 to-rose-400", tag: "bg-rose-50 text-rose-700 border-rose-100" },
                      ];
                      const c = tagColors[idx % tagColors.length];
                      return (
                        <div key={item.interest}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${c.tag}`}>
                              {item.interest}
                            </span>
                            <span className="text-xs font-bold text-gray-500">
                              {item.studentCount} {item.studentCount === 1 ? "student" : "students"}
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${c.bar} rounded-full transition-all duration-700`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
